import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Flag, Clock, Phone, User, Inbox } from 'lucide-react';
import { getStatusColor } from '../utils/statusColors';
import EmptyState from './illustrations/EmptyState';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function DriverDashboard() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const driverName = localStorage.getItem('driverName') || 'Driver';

    const fetchBookings = useCallback(async () => {
        const token = localStorage.getItem('driverToken');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            const response = await axios.get(`${API_URL}/api/driver/bookings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBookings(response.data);
            setError('');
        } catch (err) {
            setError('Failed to load bookings');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchBookings();
        const interval = setInterval(fetchBookings, 20000);
        return () => clearInterval(interval);
    }, [fetchBookings]);

    const handleEnRoute = async (id) => {
        const token = localStorage.getItem('driverToken');
        try {
            await axios.post(`${API_URL}/api/driver/bookings/${id}/en-route`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchBookings();
        } catch (err) {
            setError('Failed to update status');
        }
    };

    const handleComplete = async (id) => {
        const token = localStorage.getItem('driverToken');
        try {
            await axios.post(`${API_URL}/api/driver/bookings/${id}/complete`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchBookings();
        } catch (err) {
            setError('Failed to update status');
        }
    };

    const handleCancel = async (id) => {
        if (!window.confirm('Are you sure you want to cancel this ride?')) return;
        const token = localStorage.getItem('driverToken');
        try {
            await axios.post(`${API_URL}/api/driver/bookings/${id}/cancel`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchBookings();
        } catch (err) {
            setError('Failed to cancel booking');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('driverToken');
        localStorage.removeItem('driverName');
        navigate('/login');
    };

    // The API only ever returns this driver's own assignments, so these just
    // split that list by lifecycle stage.
    const activeBookings = bookings.filter((b) => b.status !== 'completed' && b.status !== 'cancelled');
    const completedBookings = bookings.filter((b) => b.status === 'completed' || b.status === 'cancelled');

    if (loading) {
        return <div className="dashboard-page driver-compact"><div className="loading">Loading...</div></div>;
    }

    return (
        <div className="dashboard-page driver-compact">
            <div className="dashboard-container">
                <div className="dashboard-header">
                    <div>
                        <h1>Welcome, {driverName}</h1>
                        <p>Your assigned trips</p>
                    </div>
                </div>

                <div className="filters" style={{ marginBottom: '20px', justifyContent: 'space-between', flexWrap: 'nowrap', overflowX: 'auto' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                        <Link to="/driver/dashboard" className="active" style={{ textDecoration: 'none' }}>My Rides</Link>
                        <Link to="/driver/calendar" style={{ textDecoration: 'none' }}>Calendar</Link>
                    </div>
                    <button className="logout-btn" onClick={handleLogout} style={{ flexShrink: 0 }}>Logout</button>
                </div>

                {error && <div className="error-message">{error}</div>}

                <h2 style={{ marginBottom: '16px', color: 'var(--dark)' }}>My Rides ({activeBookings.length})</h2>
                <div className="bookings-list" style={{ marginBottom: '32px' }}>
                    {activeBookings.length === 0 && <EmptyState icon={Inbox} label="No rides assigned to you yet" />}
                    {activeBookings.map((booking) => (
                        <div key={booking.id} className="booking-card">
                            <div className="booking-header">
                                <span className="booking-id">#{booking.id}</span>
                                <span className="booking-status" style={{ backgroundColor: getStatusColor(booking.status) }}>
                  {booking.status}
                </span>
                            </div>
                            <div className="booking-details">
                                <div className="detail-row">
                                    <span className="label"><User size={14} /> Customer</span>
                                    <span className="value">{booking.customer_name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label"><Phone size={14} /> Phone</span>
                                    <a className="phone-link" href={`tel:${booking.customer_phone}`}>{booking.customer_phone}</a>
                                </div>
                                <div className="detail-row">
                                    <span className="label"><MapPin size={14} /> Pickup</span>
                                    <span className="value">{booking.pickup_location}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label"><Flag size={14} /> Destination</span>
                                    <span className="value">{booking.destination}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label"><Clock size={14} /> Time</span>
                                    <span className="value">{new Date(booking.pickup_datetime).toLocaleString()}</span>
                                </div>
                            </div>
                            <div className="booking-actions" style={{ display: 'flex', gap: '12px' }}>
                                {booking.status === 'assigned' && (
                                    <button className="login-btn" onClick={() => handleEnRoute(booking.id)}>
                                        On My Way
                                    </button>
                                )}
                                {booking.status === 'en_route' && (
                                    <button className="login-btn" onClick={() => handleComplete(booking.id)}>
                                        Drive Completed
                                    </button>
                                )}
                                <button className="cancel-btn" onClick={() => handleCancel(booking.id)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <h2 style={{ marginBottom: '16px', marginTop: '32px', color: 'var(--dark)' }}>
                    Completed & Cancelled ({completedBookings.length})
                </h2>
                <div className="bookings-list">
                    {completedBookings.length === 0 && <EmptyState icon={Inbox} label="No completed rides yet" />}
                    {completedBookings.map((booking) => (
                        <div key={booking.id} className="booking-card">
                            <div className="booking-header">
                                <span className="booking-id">#{booking.id}</span>
                                <span
                                    className="booking-status"
                                    style={{ backgroundColor: getStatusColor(booking.status) }}
                                >
          {booking.status}
        </span>
                            </div>
                            <div className="booking-details">
                                <div className="detail-row">
                                    <span className="label">Customer</span>
                                    <span className="value">{booking.customer_name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Pickup</span>
                                    <span className="value">{booking.pickup_location}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Time</span>
                                    <span className="value">{new Date(booking.pickup_datetime).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default DriverDashboard;